import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ToastContext';
import {
  cancelGuestBooking,
  fetchGuestBooking,
  fetchGuestBookings,
} from '../../api/guestApi';
import { ensureGuestInvoice, listGuestInvoices } from '../../api/invoices';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'awaiting_payment', label: 'Awaiting payment' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'checked_in', label: 'Checked in' },
  { value: 'checked_out', label: 'Checked out' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancel_requested', label: 'Cancel requested' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'declined', label: 'Declined' },
  { value: 'refunded', label: 'Refunded' },
];

const paymentOptions = [
  { value: '', label: 'All payments' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partial' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'requires_payment', label: 'Requires payment' },
];

const defaultFilters = {
  status: '',
  paymentStatus: '',
  q: '',
  from: '',
  to: '',
};

const badgeStyles = {
  pending: 'bg-amber-100 text-amber-700',
  awaiting_payment: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  checked_in: 'bg-emerald-100 text-emerald-700',
  checked_out: 'bg-sky-100 text-sky-700',
  completed: 'bg-slate-100 text-slate-700',
  cancel_requested: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-rose-100 text-rose-700',
  declined: 'bg-rose-100 text-rose-700',
  refunded: 'bg-rose-100 text-rose-700',
  unpaid: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  partial: 'bg-sky-100 text-sky-700',
  failed: 'bg-rose-100 text-rose-700',
  requires_payment: 'bg-amber-100 text-amber-700',
  default: 'bg-slate-100 text-slate-700',
};

const formatDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatAmount = (currency, amount) => {
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return `${currency || ''} ${amount || ''}`.trim();
  return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const statusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ');

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = badgeStyles[key] || badgeStyles.default;
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${cls}`}>{statusLabel(value)}</span>;
};

const canCancelBooking = (booking) => {
  const status = String(booking?.booking_status || '').toLowerCase();
  if (!['pending', 'awaiting_payment', 'confirmed'].includes(status)) return false;
  if (!booking?.check_in_date) return false;
  const checkIn = new Date(booking.check_in_date);
  if (Number.isNaN(checkIn.getTime())) return false;
  return checkIn > new Date();
};

const canPayBooking = (booking) => {
  const bookingStatus = String(booking?.booking_status || '').toLowerCase();
  const paymentStatus = String(booking?.payment_status || '').toLowerCase();
  if (['cancelled', 'cancel_requested', 'declined', 'expired', 'refunded', 'completed'].includes(bookingStatus)) {
    return false;
  }
  return ['pending', 'requires_payment', 'unpaid', 'failed'].includes(paymentStatus);
};

const getPaymentBookingKey = (booking) => booking?.booking_code || booking?.id;

const GuestBookings = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [draft, setDraft] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [actionId, setActionId] = useState(null);

  const load = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchGuestBookings({
        ...nextFilters,
        page: nextPage,
        limit: pagination.limit || 10,
      });
      setItems(res.data || []);
      setPagination(res.pagination || { page: nextPage, totalPages: 1, total: 0, limit: pagination.limit || 10 });
    } catch (err) {
      setError(err.message || 'Unable to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const applyFilters = () => {
    setFilters(draft);
    setPage(1);
  };

  const resetFilters = () => {
    setDraft(defaultFilters);
    setFilters(defaultFilters);
    setPage(1);
  };

  const openDetail = async (bookingId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelected(null);
    try {
      const res = await fetchGuestBooking(bookingId);
      setSelected(res);
    } catch (err) {
      toast.error(err.message || 'Unable to load booking detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelected(null);
  };

  const handleCancel = async (booking) => {
    if (!canCancelBooking(booking)) return;
    const ok = window.confirm('Cancel this booking?');
    if (!ok) return;
    setActionId(booking.id);
    try {
      await cancelGuestBooking(booking.id);
      toast.success('Booking cancelled');
      await load(page, filters);
      if (selected?.id === booking.id) {
        const detail = await fetchGuestBooking(booking.id);
        setSelected(detail);
      }
    } catch (err) {
      toast.error(err.message || 'Cancel failed');
    } finally {
      setActionId(null);
    }
  };

  const handleInvoice = async (booking) => {
    setActionId(booking.id);
    try {
      const res = await listGuestInvoices({ bookingId: booking.id, limit: 1 });
      let invoice = res?.data?.[0];
      if (!invoice) {
        invoice = await ensureGuestInvoice(booking.id);
      }
      if (!invoice?.id) throw new Error('Invoice not available yet.');
      navigate(`/guest/invoices/${invoice.id}`);
    } catch (err) {
      toast.error(err.message || 'Invoice lookup failed');
    } finally {
      setActionId(null);
    }
  };

  const handlePayNow = (booking) => {
    const bookingKey = getPaymentBookingKey(booking);
    if (!bookingKey) return;
    navigate(`/booking/payment?bookingId=${encodeURIComponent(bookingKey)}`);
  };

  const pageSummary = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total || 0);
    if (!pagination.total) return '0 results';
    return `${start}-${end} of ${pagination.total}`;
  }, [pagination]);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Search</label>
            <input
              type="text"
              value={draft.q}
              onChange={(e) => setDraft((prev) => ({ ...prev, q: e.target.value }))}
              placeholder="Booking code"
              className="mt-1 w-64 rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Status</label>
            <select
              value={draft.status}
              onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))}
              className="mt-1 w-44 rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Payment</label>
            <select
              value={draft.paymentStatus}
              onChange={(e) => setDraft((prev) => ({ ...prev, paymentStatus: e.target.value }))}
              className="mt-1 w-44 rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {paymentOptions.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">From</label>
            <input
              type="date"
              value={draft.from}
              onChange={(e) => setDraft((prev) => ({ ...prev, from: e.target.value }))}
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">To</label>
            <input
              type="date"
              value={draft.to}
              onChange={(e) => setDraft((prev) => ({ ...prev, to: e.target.value }))}
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyFilters}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">My bookings</p>
            <p className="text-xs text-slate-500">Track stays, payments, and invoices.</p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{pageSummary}</span>
        </div>

        {error && <div className="px-4 py-3 text-sm text-rose-600">{error}</div>}
        {loading && <div className="px-4 py-3 text-sm text-slate-500">Loading bookings...</div>}

        {!loading && !error && items.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500">No bookings match your filters.</div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3">Booking</th>
                  <th className="px-4 py-3">Property</th>
                  <th className="px-4 py-3">Check-in</th>
                  <th className="px-4 py-3">Check-out</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-900">{b.booking_code}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-medium">{b.property_title || 'Property'}</div>
                      <div className="text-xs text-slate-500">{b.property_location || 'Location TBD'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{formatDate(b.check_in_date)}</td>
                    <td className="px-4 py-3 text-slate-700">{formatDate(b.check_out_date)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge value={b.booking_status} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge value={b.payment_status} />
                      <div className="mt-1 text-xs text-slate-500 capitalize">
                        {statusLabel(b.payment_option_selected || 'full')}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {formatAmount(b.currency, b.total_amount)}
                      <div className="mt-1 text-xs font-normal text-slate-500">
                        Paid {formatAmount(b.currency, b.amount_paid_now ?? b.total_amount ?? 0)} | Pending {formatAmount(b.currency, b.remaining_amount ?? 0)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => openDetail(b.id)}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-200 hover:text-emerald-700"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInvoice(b)}
                          disabled={actionId === b.id}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-200 hover:text-emerald-700 disabled:opacity-50"
                        >
                          View invoice
                        </button>
                        {canPayBooking(b) && (
                          <button
                            type="button"
                            onClick={() => handlePayNow(b)}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                          >
                            Pay now
                          </button>
                        )}
                        {canCancelBooking(b) && (
                          <button
                            type="button"
                            onClick={() => handleCancel(b)}
                            disabled={actionId === b.id}
                            className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Previous
          </button>
          <div className="text-slate-600">
            Page {pagination.page} of {pagination.totalPages}
          </div>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
            disabled={page >= pagination.totalPages}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <button
            type="button"
            onClick={closeDetail}
            className="absolute inset-0 bg-slate-950/60"
            aria-label="Close booking detail"
          />
          <div className="relative w-full max-w-4xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Booking detail</p>
                <p className="text-lg font-semibold text-slate-900">{selected?.booking_code || 'Booking'}</p>
              </div>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600"
              >
                Close
              </button>
            </div>

            {detailLoading && <div className="px-5 py-6 text-sm text-slate-500">Loading details...</div>}

            {!detailLoading && selected && (
              <div className="grid gap-5 px-5 py-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Property</p>
                    <p className="mt-2 text-lg font-semibold text-slate-900">{selected.property?.title || 'Property'}</p>
                    <p className="text-sm text-slate-600">{selected.property?.location || 'Location TBD'}</p>
                    {selected.property?.image_url && (
                      <img
                        src={selected.property.image_url}
                        alt={selected.property.title || 'Property'}
                        className="mt-3 h-40 w-full rounded-xl object-cover"
                      />
                    )}
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Stay details</p>
                    <div className="mt-3 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-slate-400">Check-in</p>
                        <p className="font-semibold">{formatDate(selected.check_in_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Check-out</p>
                        <p className="font-semibold">{formatDate(selected.check_out_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Guests</p>
                        <p className="font-semibold">{selected.guests_count || 'TBD'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Total</p>
                        <p className="font-semibold">{formatAmount(selected.currency, selected.total_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Payment mode</p>
                        <p className="font-semibold capitalize">{statusLabel(selected.payment_option_selected || 'full')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Paid now</p>
                        <p className="font-semibold">{formatAmount(selected.currency, selected.amount_paid_now ?? selected.total_amount ?? 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Pending balance</p>
                        <p className="font-semibold">{formatAmount(selected.currency, selected.remaining_amount ?? 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Status</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <StatusBadge value={selected.booking_status} />
                      <StatusBadge value={selected.payment_status} />
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Actions</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleInvoice(selected)}
                        disabled={actionId === selected.id}
                        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
                      >
                        View invoice
                      </button>
                      {canPayBooking(selected) && (
                        <button
                          type="button"
                          onClick={() => handlePayNow(selected)}
                          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                        >
                          Pay now
                        </button>
                      )}
                      {canCancelBooking(selected) && (
                        <button
                          type="button"
                          onClick={() => handleCancel(selected)}
                          disabled={actionId === selected.id}
                          className="rounded-lg bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 disabled:opacity-50"
                        >
                          Cancel booking
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestBookings;
